import { MetaImageUrlContext } from 'app/components/common/PageMeta';
import { TargetLanguageProvider } from 'app/components/LanguageContext';
import { BreadcrumbsProvider } from 'app/components/lesson/Breadcrumbs';
import { useI18n } from 'app/components/localization';
import HttpStatusPage from 'app/components/pages/HttpStatusPage';
import LessonExamPendingPage from 'app/components/pages/LessonExamPending';
import LessonExpiredPage from 'app/components/pages/LessonExpiredPage';
import { Image, Lesson, Text } from 'app/constants/schemas';
import {
  DESERIALIZATION_DETAILS_ERROR_VALID_FROM,
  ERROR_LESSON_ACCESS_EXPIRED,
  ERROR_LESSON_ACCESS_PENDING,
  ERROR_LESSON_TOKEN_INVALID,
} from 'app/constants/ServerErrors';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { useAutoLoader } from 'app/containers/state/AutoLoader';
import { throwOnError } from 'app/containers/state/makeCachedLoader';
import { LoadingStage, LoadingState } from 'app/containers/state/LoadingState';
import { fetchCategory, fetchCourse } from 'app/models/entities/remote';
import { getEntityOfType } from 'app/models/entities/selectors';
import {
  fetchCurrentPublishedLesson,
  fetchLessonBoundFunctionType,
  fetchLessonFunctionType,
  fetchPublishedLesson,
} from 'app/models/publishedLessonList/remote';
import { getCurrentLessonForLesson } from 'app/models/publishedLessonList/selectors';
import ApplicationError from 'app/models/request/ApplicationError';
import { BookingId, BookingType } from 'app/models/rtc/booking/constants';
import { debug } from 'app/services/logger';
import { useTranscodedSource } from 'app/services/transcoderService/react';
import { AppState } from 'app/store';
import { EntityStringId } from 'app/types/entity';
import { useLatestCallback } from 'app/utils/react';
import React, { FunctionComponent, ReactNode, useContext } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Language } from 'app/constants/enums/Language';
import { sleep } from 'app/utils/async';
import { CancelToken } from 'app/utils/cancelToken';
import useIsBookingInitiallyLoading from 'app/containers/rtc/BookingContext/useBookingInitiallyLoading';
import { decodeWithDefault } from 'app/utils/ioTs';
import { DateMarshallerType } from 'app/constants/schemas/Date';

const IsPublishedLessonPage = React.createContext(false);
export const useIsPublishedLessonPage = () => useContext(IsPublishedLessonPage);

const RESULT_LESSON_ACCESS_PENDING = Symbol(ERROR_LESSON_ACCESS_PENDING);
const RESULT_LESSON_ACCESS_EXPIRED = Symbol(ERROR_LESSON_ACCESS_EXPIRED);
const RESULT_LESSON_TOKEN_INVALID = Symbol(ERROR_LESSON_TOKEN_INVALID);

interface IBaseLoadingArgs {
  categoryId?: EntityStringId;
  courseId?: EntityStringId;
  lessonId: EntityStringId; // The id of a given lesson or the masterId if loading current
  tieupToken?: string;
}

interface ILoadingArgs extends IBaseLoadingArgs {
  isBookingInitiallyLoading: boolean;
  lessonBookingId?: BookingId;
}

enum LessonLoadStatus {
  Success = 'success',
  Error = 'error',
}

type LessonErrorCode = typeof RESULT_LESSON_TOKEN_INVALID |
  typeof RESULT_LESSON_ACCESS_EXPIRED |
  typeof RESULT_LESSON_ACCESS_PENDING;

interface ILoadSuccess {
  status: LessonLoadStatus.Success;
  loadedBreadcrumbs: boolean;
}

interface ILoadError {
  status: LessonLoadStatus.Error;
  code: LessonErrorCode;
  error?: ApplicationError;
}

type LessonLoadResult = ILoadSuccess | ILoadError;

interface IChildrenProps {
  children: (
    loadingStateArgs: LoadingState<LessonLoadResult>,
    args: { id?: EntityStringId, title?: string }
  ) => ReactNode;
}

interface IProps extends IChildrenProps {
  children: (
    loadingStateArgs: LoadingState<LessonLoadResult>,
    args: { id?: EntityStringId, title?: string }
  ) => ReactNode;
  currentId?: EntityStringId;
  fetchLesson: fetchLessonBoundFunctionType;
  language: Language;
  lessonImageUrl: string;
  loadArgs: ILoadingArgs;
  title?: string;
}

const PublishedLessonLoadingStatePage: FunctionComponent<IProps> = ({
  children,
  currentId,
  language,
  fetchLesson,
  lessonImageUrl,
  loadArgs,
  title,
}) => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  /**
   * Loads the information necessary for rendering breadcrumbs. This will always resolve, regardless of
   * whether or not the categoryId or courseId loads.
   *
   * @returns Resolves to true if breadcrumb data successfully loaded, false otherwise.
   */
  const loadBreadcrumbs = useLatestCallback(async (categoryId?: EntityStringId, courseId?: EntityStringId) => {
    if (categoryId && courseId) {
      try {
        await Promise.all([
          dispatch(fetchCategory(categoryId)),
          dispatch(fetchCourse(courseId)),
        ]);

        return true;
      } catch (err) {
        debug('Failed to load breadcrumb data.', { categoryId, courseId });
      }
    }

    return false;
  });

  const load = useLatestCallback(async (
    {
      categoryId,
      courseId,
      isBookingInitiallyLoading,
      lessonBookingId,
      lessonId,
      tieupToken,
    }: ILoadingArgs,
    cancelToken: CancelToken,
  ): Promise<LessonLoadResult> => {
    // If the booking is loading just sleep indefinitely and let load trigger again once the booking has finished.
    // This will prevent race conditions for tie up lessons.
    if (isBookingInitiallyLoading) {
      debug('Booking is potentially loading, sleeping until booking has loaded.');
      await sleep(Infinity, cancelToken);
    }

    debug('Loading lesson data.', { categoryId, courseId, lessonId, tieupToken });

    try {
      // Load breadcrumb information and lesson data at the same time. But, only
      // return information on the current lesson.
      const [, loadedBreadcrumbs] = await Promise.all([
        fetchLesson(lessonId, tieupToken, lessonBookingId),
        loadBreadcrumbs(categoryId, courseId),
      ]);
      return { status: LessonLoadStatus.Success, loadedBreadcrumbs };
    } catch (error) {
      if (error instanceof ApplicationError && error.code === ERROR_LESSON_ACCESS_PENDING) {
        return { status: LessonLoadStatus.Error, code: RESULT_LESSON_ACCESS_PENDING, error };
      }

      if (error instanceof ApplicationError && error.code === ERROR_LESSON_ACCESS_EXPIRED) {
        return { status: LessonLoadStatus.Error, code: RESULT_LESSON_ACCESS_EXPIRED };
      }

      if (error instanceof ApplicationError && error.code === ERROR_LESSON_TOKEN_INVALID) {
        return { status: LessonLoadStatus.Error, code: RESULT_LESSON_TOKEN_INVALID };
      }

      // Fallback to throwing the error if it can't be handled.
      throw error;
    }
  });

  const loadingState = throwOnError(useAutoLoader({ args: loadArgs, load }));

  if (loadingState.stage === LoadingStage.Success && loadingState.value.status === LessonLoadStatus.Error) {
    const { value } = loadingState;
    if (value.code === RESULT_LESSON_ACCESS_PENDING) {
      const validFrom = value.error?.meta?.get(DESERIALIZATION_DETAILS_ERROR_VALID_FROM);
      const validDate = decodeWithDefault(DateMarshallerType, validFrom, undefined);
      return (
        <LessonExamPendingPage loadLesson={loadingState.reload} validFrom={validDate} />
      );
    }

    if (value.code === RESULT_LESSON_ACCESS_EXPIRED) {
      return (
        <LessonExpiredPage />
      );
    }

    if (value.code === RESULT_LESSON_TOKEN_INVALID) {
      return (
        <HttpStatusPage
          status={400}
          header={t('http-status-page.tieup.invalid-token.header')}
          message={t('http-status-page.tieup.invalid-token.message')}
        />
      );
    }

    return null;
  }

  // Set up the breadcrumbs if they are available.
  const { categoryId, courseId } = loadArgs;
  const breadcrumbsProps =
    loadingState.stage === LoadingStage.Success && loadingState.value.status === LessonLoadStatus.Success &&
      loadingState.value.loadedBreadcrumbs && categoryId && courseId ?
        { categoryId, courseId } :
        null;

  const page = (
    <TargetLanguageProvider language={language}>
      <BreadcrumbsProvider value={breadcrumbsProps}>
        {children(loadingState, { id: currentId, title })}
      </BreadcrumbsProvider>
    </TargetLanguageProvider>
  );

  // If lesson has an image, provide the transcoded image URL to <Page> so it can be
  // used to set the meta tags.
  if (lessonImageUrl) {
    return (<TranscodedImageMetaImageUrlProvider children={page} src={lessonImageUrl} />);
  }

  return page;
};

const TranscodedImageMetaImageUrlProvider: FunctionComponent<{ src: string }> = ({ children, src }) => {
  const transcodedSrc = useTranscodedSource(src, { height: 500, width: 500 });
  return (<MetaImageUrlContext.Provider children={children} value={transcodedSrc} />);
};

interface IInjectDependenciesProps extends IBaseLoadingArgs, IChildrenProps {}

const makeConnectedLessonComponent = (fetchLesson: fetchLessonFunctionType) => {
  const mapStateToProps = (state: AppState, props: ILoadingArgs) => {
    const { categoryId, courseId, isBookingInitiallyLoading, lessonId, tieupToken, lessonBookingId } = props;
    const currentId = getCurrentLessonForLesson(state, lessonId);
    const lesson = getEntityOfType(state, Lesson, currentId);
    const title = lesson && getEntityOfType(state, Text, lesson.titleText);
    const image = lesson && getEntityOfType(state, Image, lesson.image);
    const language = lesson && lesson.language;

    return {
      loadArgs: {
        categoryId,
        courseId,
        isBookingInitiallyLoading,
        lessonBookingId,
        lessonId,
        tieupToken,
      },
      currentId,
      title: title && title.text,
      lessonImageUrl: image && image.url,
      language,
    };
  };

  const mapDispatchToProps = {
    fetchLesson,
  };

  const Container = connect(mapStateToProps, mapDispatchToProps)(PublishedLessonLoadingStatePage);

  const InjectDependencies: React.FunctionComponent<IInjectDependenciesProps> = ({ lessonId, ...props }) => {
    const bookingState = useBookingState();
    const isBookingInitiallyLoading = useIsBookingInitiallyLoading();

    let lessonBookingId;

    if (bookingState?.bookingType === BookingType.Lesson) {
      lessonBookingId = bookingState.tieUps[lessonId];
    }

    return (
      // eslint-disable-next-line react/jsx-boolean-value
      <IsPublishedLessonPage.Provider value={true}>
        <Container
          isBookingInitiallyLoading={isBookingInitiallyLoading}
          lessonId={lessonId}
          lessonBookingId={lessonBookingId}
          {...props}
        />
      </IsPublishedLessonPage.Provider>
    );
  };

  return InjectDependencies;
};

const ConnectedPublishedLessonLoadingStatePage = makeConnectedLessonComponent(fetchCurrentPublishedLesson);

export const SpecificLessonLoadingStatePage = makeConnectedLessonComponent(fetchPublishedLesson);

export type LessonLoadingStatePageType = typeof ConnectedPublishedLessonLoadingStatePage |
  typeof SpecificLessonLoadingStatePage;

export default ConnectedPublishedLessonLoadingStatePage;
